import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { MapPin, LogOut, User, Package, ShieldCheck, Menu, X, Heart, Home as HomeIcon, Sun, Moon, Monitor } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { NAV } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useTheme } from "@/context/ThemeContext";

const THEME_OPTIONS = [
  { key: "light", label: "Light", icon: Sun },
  { key: "dark", label: "Dark", icon: Moon },
  { key: "system", label: "System", icon: Monitor },
];

function ThemeSwitcher({ compact = false }) {
  const { pref, resolved, setPreference } = useTheme();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="theme-switcher-btn"
          title={`Theme: ${pref === "default" ? "Store default" : pref}`}
          className="p-2 rounded-full border border-hyd-border text-hyd-text2 hover:text-hyd-gold hover:border-hyd-gold/60 transition-colors"
        >
          {resolved === "light" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-hyd-panel border-hyd-border min-w-[150px]">
        {THEME_OPTIONS.map((o) => (
          <DropdownMenuItem
            key={o.key}
            data-testid={`theme-option-${o.key}`}
            onClick={() => setPreference(o.key)}
            className={`flex items-center gap-2 text-sm cursor-pointer ${pref === o.key ? "text-hyd-gold" : "text-hyd-text2"}`}
          >
            <o.icon className="w-4 h-4" /> {o.label}
            {pref === o.key && <span className="ml-auto text-[9px] uppercase font-bold">On</span>}
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem
          data-testid="theme-option-default"
          onClick={() => setPreference(null)}
          className={`flex items-center gap-2 text-sm cursor-pointer border-t border-hyd-border mt-1 pt-2 ${pref === "default" ? "text-hyd-gold" : "text-hyd-text2"}`}
        >
          <ShieldCheck className="w-4 h-4" /> Store default
          {pref === "default" && <span className="ml-auto text-[9px] uppercase font-bold">On</span>}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileThemeRow() {
  const { pref, setPreference } = useTheme();
  return (
    <div className="flex gap-1 bg-hyd-panel border border-hyd-border rounded-full p-1">
      {THEME_OPTIONS.map((o) => (
        <button
          key={o.key}
          data-testid={`theme-option-mobile-${o.key}`}
          onClick={() => setPreference(o.key)}
          className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold ${pref === o.key ? "bg-hyd-gold text-hyd-bg" : "text-hyd-text2"}`}
        >
          <o.icon className="w-3 h-3" /> {o.label}
        </button>
      ))}
    </div>
  );
}

export function Header({ onOpenPincode }) {
  const { user, logout } = useAuth();
  const { totals, pincode } = useCart();
  const { count: wishCount } = useWishlist();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-hyd-bg/80 border-b border-hyd-border">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
        <Logo />

        {/* Pincode chip */}
        <button
          data-testid={NAV.pincodeChip}
          onClick={onOpenPincode}
          className="ml-auto sm:ml-4 flex items-center gap-1.5 text-xs sm:text-sm text-hyd-text2 hover:text-hyd-gold transition-colors px-2.5 py-1.5 rounded-full border border-hyd-border"
        >
          <MapPin className="w-3.5 h-3.5" />
          <span className="hidden xs:inline">{pincode ? `Deliver to ${pincode}` : "Set pincode"}</span>
          <span className="xs:hidden">{pincode || "Pincode"}</span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1 text-sm">
          <Link to="/" data-testid={NAV.home} className="px-3 py-1.5 rounded-md hover:bg-hyd-panel text-hyd-text2 hover:text-hyd-gold">Home</Link>
          <Link to="/standard_crackers_pricelist" data-testid="nav-quick-order" className="px-3 py-1.5 rounded-md hover:bg-hyd-panel text-hyd-gold flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5" /> Quick Order
          </Link>
          <Link to="/combos" data-testid={NAV.combos} className="px-3 py-1.5 rounded-md hover:bg-hyd-panel text-hyd-text2 hover:text-hyd-gold">Combos</Link>
          {user && (
            <Link to="/wishlist" data-testid={NAV.wishlistBadge} className="px-3 py-1.5 rounded-md hover:bg-hyd-panel text-hyd-text2 hover:text-hyd-red flex items-center gap-1">
              <Heart className={`w-3.5 h-3.5 ${wishCount > 0 ? "fill-hyd-red text-hyd-red" : ""}`} />
              {wishCount > 0 && <span>{wishCount}</span>}
            </Link>
          )}
          {user && (
            <Link to="/orders" data-testid={NAV.orders} className="px-3 py-1.5 rounded-md hover:bg-hyd-panel text-hyd-text2 hover:text-hyd-gold">
              Orders
            </Link>
          )}
          {user && (
            <Link to="/account/addresses" data-testid="nav-addresses" className="px-3 py-1.5 rounded-md hover:bg-hyd-panel text-hyd-text2 hover:text-hyd-gold flex items-center gap-1">
              <HomeIcon className="w-3.5 h-3.5" /> Addresses
            </Link>
          )}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid={NAV.admin} className="px-3 py-1.5 rounded-md text-hyd-gold hover:bg-hyd-panel flex items-center gap-1">
              <ShieldCheck className="w-4 h-4" /> Admin
            </Link>
          )}
        </nav>

        {/* Auth */}
        <div className="flex items-center gap-2">
          <ThemeSwitcher />
          {user ? (
            <button
              data-testid={NAV.logoutBtn}
              onClick={() => { logout(); navigate("/"); }}
              className="hidden sm:flex items-center gap-1 text-xs text-hyd-text2 hover:text-hyd-red transition-colors px-2 py-1.5"
              title={user.phone}
            >
              <LogOut className="w-4 h-4" /> <span className="hidden md:inline">Logout</span>
            </button>
          ) : (
            <Link
              to="/login"
              data-testid={NAV.loginBtn}
              className="text-xs sm:text-sm font-bold uppercase tracking-wider bg-hyd-gold text-hyd-bg px-3 py-1.5 rounded-full hover:bg-hyd-goldDim transition-colors flex items-center gap-1"
            >
              <User className="w-3.5 h-3.5" /> Login
            </Link>
          )}
          {totals.count > 0 && (
            <Link
              to="/cart"
              data-testid={NAV.cartBadge}
              className="relative p-2 rounded-full bg-hyd-panel border border-hyd-border hover:border-hyd-gold"
            >
              <Package className="w-4 h-4 text-hyd-gold" />
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-hyd-red text-white text-[10px] font-bold grid place-items-center">
                {totals.count}
              </span>
            </Link>
          )}
          <button className="md:hidden p-2 text-hyd-text2" onClick={() => setMenuOpen((v) => !v)}>
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-hyd-border bg-hyd-bg px-4 py-3 flex flex-col gap-2 text-sm">
          <div className="flex items-center gap-2 py-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-hyd-text3">Theme</span>
            <MobileThemeRow />
          </div>
          <Link onClick={() => setMenuOpen(false)} to="/" className="py-2 text-hyd-text2 hover:text-hyd-gold">Home</Link>
          <Link onClick={() => setMenuOpen(false)} to="/standard_crackers_pricelist" data-testid="nav-quick-order-mobile" className="py-2 text-hyd-gold">Quick Order</Link>
          <Link onClick={() => setMenuOpen(false)} to="/combos" className="py-2 text-hyd-text2 hover:text-hyd-gold">Combos</Link>
          {user && <Link onClick={() => setMenuOpen(false)} to="/wishlist" className="py-2 text-hyd-text2 hover:text-hyd-red">Wishlist {wishCount > 0 ? `(${wishCount})` : ""}</Link>}
          {user && <Link onClick={() => setMenuOpen(false)} to="/orders" className="py-2 text-hyd-text2 hover:text-hyd-gold">My Orders</Link>}
          {user && <Link onClick={() => setMenuOpen(false)} to="/account/addresses" className="py-2 text-hyd-text2 hover:text-hyd-gold">Addresses</Link>}
          {user?.role === "admin" && <Link onClick={() => setMenuOpen(false)} to="/admin" className="py-2 text-hyd-gold">Admin</Link>}
          {user && (
            <button onClick={() => { logout(); setMenuOpen(false); navigate("/"); }} className="py-2 text-left text-hyd-red">
              Logout
            </button>
          )}
        </div>
      )}
    </header>
  );
}
